import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '../../components/Button/Button'
import Link from '../../components/Link/Link'
import TextInput from '../../components/TextInput/TextInput'
import sso22Axios from '../../utility/sso22Axios'
import './../../styles/Pages/LoginPage.sass'
import { ResetPasswordPageParams } from './RequestResetPasswordPage'
import LoadingModal from '../../components/Modals/LoadingModal'
import { useTranslation } from 'react-i18next'
import { emailRegEx, passwordRegEx } from '../../utility/functions'
import { ROUTES } from '../../resources/routes-constants'
import { COLORS } from '../../resources/colors'
import { UserInfo } from '../../types/user'
import { useDispatch } from 'react-redux'
import { setLoggedUserData } from '../../store/actions/user'

const LoginPage: React.FC = () => {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loginError, setLoginError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const routeParams = useMemo(() => {
        const params = {
            state: {
                userEmail: email,
            } as ResetPasswordPageParams,
        }
        return params
    }, [email])

    const logUser = async () => {
        setIsLoading(true)
        try {
            const loginCredentials = {
                email,
                password,
            }
            const loggedUserData: UserInfo = await sso22Axios
                .post('auth/login', loginCredentials)
                .then((response) => response.data)

            if (loggedUserData.uuid) {
                dispatch(setLoggedUserData(loggedUserData))
                navigate(`${ROUTES.HOMEPAGE_ROUTE}`)
            }
        } catch (error) {
            console.error(error)
            setLoginError((error as string).toString())
        }
        setIsLoading(false)
    }

    return (
        <div className="u-full-page-container u-row-page" style={{ gap: 0 }}>
            <div className="c-login-interactive-panel">
                <div className="c-login-interactive-panel__main-panel">
                    <form
                        className="o-login-inputs-container"
                        onSubmit={async (event) => {
                            event.preventDefault()
                            if (!isLoading && email && password) {
                                await logUser()
                            }
                        }}
                    >
                        <p className="u-medium-text u-bold-text">{t('auth.login')}</p>
                        <div className="u-normal-text">
                            {t('auth.no_have_account')}{' '}
                            <Link
                                label={t('auth.register')}
                                underline="always"
                                type="accent"
                                target="internal"
                                targetUrl={{ url: ROUTES.REGISTER_ROUTE }}
                            />
                        </div>
                        <TextInput
                            value={email}
                            placeholder={t('auth.email') || ''}
                            borderColor={email && !emailRegEx.test(email) ? COLORS.redDelete : ''}
                            onTextChange={(newValue) => setEmail(newValue)}
                        />
                        <TextInput
                            value={password}
                            placeholder={t('auth.password') || ''}
                            type="password"
                            borderColor={password && !passwordRegEx.test(password) ? COLORS.redDelete : ''}
                            onTextChange={(newValue) => setPassword(newValue)}
                        />
                        <p className="u-normal-text u-bold-text">
                            {t('auth.terms_accept_pre')}{' '}
                            <a href="https://www.fm-world.it/privacy/" target="_blank" rel="noreferrer">
                                {t('auth.privacy_policy')}
                            </a>
                            {t('auth.terms_accept_post')}
                        </p>
                        {loginError && (
                            <p className="u-normal-text u-bold-text u-error-text">
                                {t('general.error')}: {t('errors.datas_inserted_not_correct')}.
                            </p>
                        )}
                        <Button
                            label={t('auth.logon')}
                            type="submit"
                            onPress={async () => {
                                if (
                                    !isLoading &&
                                    email &&
                                    emailRegEx.test(email) &&
                                    password &&
                                    passwordRegEx.test(password)
                                ) {
                                    await logUser()
                                }
                            }}
                            disabled={!email || !password}
                        />
                        <Link
                            label={t('auth.forgot_password')}
                            underline="always"
                            color={COLORS.purple}
                            type="hover"
                            target="internal"
                            targetUrl={{ url: ROUTES.REQUEST_RESET_PASSWORD_ROUTE, params: routeParams }}
                        />
                    </form>
                </div>
            </div>
            <LoadingModal isVisible={isLoading} text={t('loadings.logging_in')} />
        </div>
    )
}

export default LoginPage
